// src/redux/slices/messageCounterSlice.js
"use client";

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl, getAuthHeaders } from "../../Utils/serverSelector";

// ---------- AUTH HELPERS ----------
const authJsonConfig = async () => {
  const headers = await getAuthHeaders();
  return {
    headers,
    withCredentials: true,
    validateStatus: () => true, // сами разрулим статус
  };
};

const authJsonConfigWithContentType = async () => {
  const baseCfg = await authJsonConfig();
  return {
    ...baseCfg,
    headers: {
      ...baseCfg.headers,
      "Content-Type": "application/json",
    },
  };
};

// ---------- THUNKS ----------

// Создание счетчиков сообщений
export const createMessageCounters = createAsyncThunk(
  "messageCounter/createMessageCounters",
  async ({ uid, isMaster }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const cfg = await authJsonConfigWithContentType();
      const res = await axios.post(
        `${getServerUrl()}/messageCounter`,
        { uid, isMaster },
        cfg
      );

      if (res.status !== 200 && res.status !== 201) {
        throw new Error(
          res.data?.message ||
          `Failed to create message counters (HTTP ${res.status})`
        );
      }

      return res.data;
    } catch (e) {
      return rejectWithValue(
        e?.response?.data?.message ||
        e?.response?.data ||
        e.message ||
        "Create message counters failed"
      );
    }
  }
);

// Получение счетчиков по uid
export const fetchMessageCounters = createAsyncThunk(
  "messageCounter/fetchMessageCounters",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const cfg = await authJsonConfig();
      const res = await axios.get(
        `${getServerUrl()}/messageCounter/${uid}`,
        cfg
      );

      if (res.status === 404) {
        // Вернем пустые счетчики, как и раньше
        return {
          smsCounter: 0,
          whatsappCounter: 0,
          telegramCounter: 0,
          viberCounter: 0,
          isFirstCampaignUsed: false,
          isSms: false,
          isWhatsApp: false,
          isTelegram: false,
          isViber: false,
          messagesSent: 0,
        };
      }

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          `Failed to fetch message counters (HTTP ${res.status})`
        );
      }

      return res.data;
    } catch (e) {
      return rejectWithValue(
        e?.response?.data?.message ||
        e?.response?.data ||
        e.message ||
        "Fetch message counters failed"
      );
    }
  }
);

// Массовая отправка сообщений (уменьшает счетчики)
export const sendBulkMessages = createAsyncThunk(
  "messageCounter/sendBulkMessages",
  async ({ uid, type, messagesCount }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      if (!type) throw new Error("Type not provided");

      const cfg = await authJsonConfigWithContentType();
      const res = await axios.post(
        `${getServerUrl()}/messageCounter/bulk/${type}/${uid}`,
        { messagesCount },
        cfg
      );

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          `Failed to send bulk messages (HTTP ${res.status})`
        );
      }

      // ожидаем { remaining, totalSent }
      return { ...res.data, type };
    } catch (e) {
      return rejectWithValue(
        e?.response?.data?.message ||
        e?.response?.data ||
        e.message ||
        "Bulk send failed"
      );
    }
  }
);

// Покупка сообщений (устанавливает новый total)
export const buyMessages = createAsyncThunk(
  "messageCounter/buyMessages",
  async ({ uid, type, count }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      if (!type) throw new Error("Type not provided");

      const cfg = await authJsonConfigWithContentType();
      const res = await axios.post(
        `${getServerUrl()}/messageCounter/buy/${type}/${uid}`,
        { count },
        cfg
      );

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          `Failed to buy messages (HTTP ${res.status})`
        );
      }

      // ожидаем { total }
      return { ...res.data, type };
    } catch (e) {
      return rejectWithValue(
        e?.response?.data?.message ||
        e?.response?.data ||
        e.message ||
        "Buy messages failed"
      );
    }
  }
);

// Отметить, что первый бесплатный запуск кампании использован
export const markFirstCampaignUsed = createAsyncThunk(
  "messageCounter/markFirstCampaignUsed",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const cfg = await authJsonConfigWithContentType();
      const res = await axios.post(
        `${getServerUrl()}/messageCounter/first-used/${uid}`,
        {},
        cfg
      );

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          `Failed to mark first campaign used (HTTP ${res.status})`
        );
      }

      return res.data; // ожидаем что вернется актуальное состояние/флаг
    } catch (e) {
      return rejectWithValue(
        e?.response?.data?.message ||
        e?.response?.data ||
        e.message ||
        "Mark first campaign used failed"
      );
    }
  }
);

// Получить счетчики по ownerUid (с автосозданием)
export const fetchMessageCountersByOwnerUid = createAsyncThunk(
  "messageCounter/fetchMessageCountersByOwnerUid",
  async ({ ownerUid, autocreate = false, isMaster = false }, { rejectWithValue }) => {
    try {
      if (!ownerUid) throw new Error("ownerUid не передан");

      const qs = autocreate
        ? `?autocreate=1${isMaster ? "&isMaster=1" : ""}`
        : "";

      const cfg = await authJsonConfig();
      // validateStatus мы уже задали в cfg
      const res = await axios.get(
        `${getServerUrl()}/messageCounter/owner/${ownerUid}${qs}`,
        cfg
      );

      if (res.status === 404) {
        return {
          ownerUid,
          data: {
            smsCounter: 0,
            whatsappCounter: 0,
            telegramCounter: 0,
            viberCounter: 0,
            isFirstCampaignUsed: false,
            isSms: false,
            isWhatsApp: false,
            isTelegram: false,
            isViber: false,
            messagesSent: 0,
          },
        };
      }

      if (res.status !== 200 || !res.data) {
        return rejectWithValue(`Fetch owner counters failed: ${res.status}`);
      }

      return { ownerUid, data: res.data };
    } catch (e) {
      return rejectWithValue(
        e?.response?.data?.message ||
        e?.response?.data ||
        e.message ||
        "Fetch owner counters failed"
      );
    }
  }
);

// ---------- STATE ----------

const baseState = {
  smsCounter: 0,
  whatsappCounter: 0,
  telegramCounter: 0,
  viberCounter: 0,
  isFirstCampaignUsed: false,
  isSms: false,
  isWhatsApp: false,
  isTelegram: false,
  isViber: false,
  messagesSent: 0,
};

const initialState = {
  ...baseState,
  byUid: {}, // { [ownerUid]: counters }
  byUidStatus: {}, // { [ownerUid]: 'idle'|'loading'|'succeeded'|'failed' }
  status: "idle",
  error: null,
};

// ---------- SLICE ----------

const messageCounterSlice = createSlice({
  name: "messageCounter",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchMessageCounters
      .addCase(fetchMessageCounters.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchMessageCounters.fulfilled, (state, action) => {
        state.status = "succeeded";
        Object.assign(state, action.payload || baseState);
      })
      .addCase(fetchMessageCounters.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error?.message || "Fetch failed";
      })

      // createMessageCounters
      .addCase(createMessageCounters.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createMessageCounters.fulfilled, (state, action) => {
        state.status = "succeeded";
        Object.assign(state, action.payload || {});
      })
      .addCase(createMessageCounters.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error?.message || "Create failed";
      })

      // sendBulkMessages
      .addCase(sendBulkMessages.pending, (state) => {
        state.error = null;
      })
      .addCase(sendBulkMessages.fulfilled, (state, action) => {
        const { remaining, totalSent, type } = action.payload || {};
        if (type) {
          const key = `${type}Counter`;
          state[key] =
            typeof remaining === "number" ? remaining : state[key];
        }
        if (typeof totalSent === "number") state.messagesSent = totalSent;
        state.status = "succeeded";
      })
      .addCase(sendBulkMessages.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error?.message || "Bulk send failed";
      })

      // buyMessages
      .addCase(buyMessages.pending, (state) => {
        state.error = null;
      })
      .addCase(buyMessages.fulfilled, (state, action) => {
        const { total, type } = action.payload || {};
        if (type && typeof total === "number") {
          const key = `${type}Counter`;
          state[key] = total;
        }
      })
      .addCase(buyMessages.rejected, (state, action) => {
        state.error =
          action.payload || action.error?.message || "Buy failed";
      })

      // markFirstCampaignUsed
      .addCase(markFirstCampaignUsed.pending, (state) => {
        state.error = null;
      })
      .addCase(markFirstCampaignUsed.fulfilled, (state, action) => {
        // если сервер вернет полный объект — сольем; если только флаг — отметим флаг
        if (typeof action.payload === "object" && action.payload) {
          if ("isFirstCampaignUsed" in action.payload) {
            state.isFirstCampaignUsed = !!action.payload.isFirstCampaignUsed;
          } else {
            Object.assign(state, action.payload);
          }
        } else {
          state.isFirstCampaignUsed = true;
        }
      })
      .addCase(markFirstCampaignUsed.rejected, (state, action) => {
        state.error =
          action.payload ||
          action.error?.message ||
          "Mark first campaign failed";
      })

      // fetchMessageCountersByOwnerUid
      .addCase(fetchMessageCountersByOwnerUid.pending, (state, action) => {
        const { ownerUid } = action.meta.arg || {};
        if (ownerUid) state.byUidStatus[ownerUid] = "loading";
      })
      .addCase(fetchMessageCountersByOwnerUid.fulfilled, (state, action) => {
        const { ownerUid, data } = action.payload || {};
        if (ownerUid) {
          state.byUid[ownerUid] = data || { ...baseState };
          state.byUidStatus[ownerUid] = "succeeded";
        }
      })
      .addCase(fetchMessageCountersByOwnerUid.rejected, (state, action) => {
        const { ownerUid } = action.meta.arg || {};
        if (ownerUid) state.byUidStatus[ownerUid] = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Fetch owner counters failed";
      })

      // reset
      .addCase(resetStore, () => initialState);
  },
});

export default messageCounterSlice.reducer;
